import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AppContext } from "../application/provider";
import { Button, Form, FormGroup, Input, Label, Modal, ModalBody, ModalHeader, Table } from "reactstrap";
import { FileMinus, FileText, Printer, Plus, Trash2 } from "lucide-react";
import jsPDF from "jspdf";
import ProcesoRequerimientosService from "../axios_services/procesorequerimientos.service";
import ProveedoresService from "../axios_services/proveedores.service";
import ProveedorServiciosService from "../axios_services/proveedorservicios.service";
import Loader from "../components/Loader/Loader";
import { ButtonDelete, ButtonInsert, ButtonSearch } from "../components/Buttons/Buttons";
import ModalConfirmar from "../components/Modal/ModalConfirmar";
import "./ProcesoRequerimientos.css";

const ESTADOS = [
    { id: 1, nombre: "Pendiente jefe inmediato" },
    { id: 2, nombre: "Observado por jefe" },
    { id: 3, nombre: "Pendiente logística" },
    { id: 4, nombre: "Pendiente subgerencia" },
    { id: 5, nombre: "Pendiente pago" },
    { id: 6, nombre: "Finalizado" },
    { id: 7, nombre: "Observado por subgerencia" },
];

const FORM_REQUERIMIENTO_INICIAL = {
    tipo_requerimiento: "servicio",
    descripcion_solicitud: "",
};

const DETALLE_COMPRA_INICIAL = {
    no_parte_tipo_modelo: "",
    descripcion_producto: "",
    precio_unitario: "",
    cantidad: 1,
};

const FORM_LOGISTICA_INICIAL = {
    fecha_orden: new Date().toISOString().slice(0, 10),
    proveedor_id: "",
    servicio_id: "",
    cantidad: 1,
    descripcion_orden: "",
    precio_unitario: "",
    incluye_igv: false,
    igv: 0,
    detalles: [{ ...DETALLE_COMPRA_INICIAL }],
};

const CLIENTE = {
    razonSocial: "MEC BUSINESS PARTNER",
    ciudad: "HUANCAYO",
    direccion: "CAL. SAN MARTIN LT. 1 MZ. H URB. PALIAN",
    ruc: "20612796484",
};

function ProcesoRequerimientos() {
    const [state] = useContext(AppContext);
    const [registros, setRegistros] = useState([]);
    const [proveedores, setProveedores] = useState([]);
    const [servicios, setServicios] = useState([]);
    const [loading, setLoading] = useState(false);
    const [periodo, setPeriodo] = useState(() => new Date().toISOString().slice(0, 7));
    const [estado, setEstado] = useState("");
    const [modalRequerimiento, setModalRequerimiento] = useState(false);
    const [modalLogistica, setModalLogistica] = useState(false);
    const [modalAccion, setModalAccion] = useState(false);
    const [registroSeleccionado, setRegistroSeleccionado] = useState(null);
    const [formRequerimiento, setFormRequerimiento] = useState(FORM_REQUERIMIENTO_INICIAL);
    const [formLogistica, setFormLogistica] = useState(FORM_LOGISTICA_INICIAL);
    const [accion, setAccion] = useState("");
    const [observacionAccion, setObservacionAccion] = useState("");
    const [fechaPago, setFechaPago] = useState(new Date().toISOString().slice(0, 10));
    const [archivoPago, setArchivoPago] = useState(null);
    const [mensajeModal, setMensajeModal] = useState({ isOpen: false, titulo: "", mensaje: "", variante: "exito" });
    const [confirmacionModal, setConfirmacionModal] = useState({ isOpen: false, titulo: "", mensaje: "", textConfirmar: "Confirmar", variante: "exito", onConfirmar: null });
    const initialFetchDone = useRef(false);

    const perfilId = Number(state.user?.perfil_id || state.perfil_id);
    const usuarioId = Number(state.user?.usuario_id);
    const puedeLogistica = [8, 11, 21, 26].includes(perfilId);
    const puedeTesoreria = [8, 12, 21, 26].includes(perfilId);
    const puedeSubgerencia = [8, 26].includes(perfilId);

    const formatoSoles = (monto) => new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN" }).format(Number(monto || 0));

    const formatoFecha = (fecha) => {
        if (!fecha) return "-";
        const date = new Date(fecha);
        if (isNaN(date.getTime())) return "-";
        return date.toLocaleDateString("es-PE", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: "UTC" });
    };

    const mostrarMensaje = useCallback((titulo, mensaje, variante = "exito") => {
        setMensajeModal({ isOpen: true, titulo, mensaje, variante });
    }, []);

    const obtenerMensajeError = (error, fallback) => {
        const errores = error.response?.data?.errors;
        if (errores) return Object.values(errores).flat().join("\n");
        return error.response?.data?.message || error.response?.data?.error || fallback;
    };

    const totalRequerimientos = useMemo(() => registros.reduce((acc, item) => acc + Number(item.total || 0), 0), [registros]);

    const netoCompra = useMemo(() => {
        return formLogistica.detalles.reduce((acc, item) => acc + Number(item.precio_unitario || 0) * Number(item.cantidad || 0), 0);
    }, [formLogistica.detalles]);

    const totalCompra = netoCompra + Number(formLogistica.igv || 0);
    const totalServicio = Number(formLogistica.precio_unitario || 0) * Number(formLogistica.cantidad || 1);

    const loadImg = (src) => new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = () => resolve(null);
        img.src = src;
    });

    const cargarCatalogosLogistica = useCallback(async () => {
        if (!puedeLogistica) return;
        const [proveedoresResponse, serviciosResponse] = await Promise.all([
            ProveedoresService.getProveedores(),
            ProveedorServiciosService.getServiciosProveedor(),
        ]);
        setProveedores(proveedoresResponse.data || []);
        setServicios(serviciosResponse.data || []);
    }, [puedeLogistica]);

    const cargarRegistros = useCallback(async () => {
        setLoading(true);
        try {
            const response = await ProcesoRequerimientosService.getProcesoRequerimientos(periodo, estado);
            setRegistros(response.data || []);
        } catch (error) {
            mostrarMensaje("Error", obtenerMensajeError(error, "Error al cargar requerimientos"), "peligro");
            setRegistros([]);
        } finally {
            setLoading(false);
        }
    }, [periodo, estado, mostrarMensaje]);

    useEffect(() => {
        if (!initialFetchDone.current) {
            cargarRegistros();
            if (puedeLogistica) {
                cargarCatalogosLogistica().catch(() => {});
            }
            initialFetchDone.current = true;
        }
    }, [cargarRegistros, cargarCatalogosLogistica, puedeLogistica]);

    const abrirNuevo = () => {
        setFormRequerimiento(FORM_REQUERIMIENTO_INICIAL);
        setModalRequerimiento(true);
    };

    const cerrarRequerimiento = () => {
        setModalRequerimiento(false);
        setFormRequerimiento(FORM_REQUERIMIENTO_INICIAL);
    };

    const abrirLogistica = async (registro) => {
        try {
            await cargarCatalogosLogistica();
        } catch (error) {
            mostrarMensaje("Error", obtenerMensajeError(error, "Error al cargar catálogos de logística"), "peligro");
            return;
        }

        const detalles = registro.detalles?.length
            ? registro.detalles.map(item => ({
                no_parte_tipo_modelo: item.no_parte_tipo_modelo || "",
                descripcion_producto: item.descripcion_producto || "",
                precio_unitario: item.precio_unitario || "",
                cantidad: item.cantidad || 1,
            }))
            : [{ ...DETALLE_COMPRA_INICIAL }];

        setRegistroSeleccionado(registro);
        setFormLogistica({
            fecha_orden: registro.fecha_orden ? String(registro.fecha_orden).slice(0, 10) : new Date().toISOString().slice(0, 10),
            proveedor_id: registro.proveedor_id || "",
            servicio_id: registro.servicio_id || "",
            cantidad: registro.cantidad || 1,
            descripcion_orden: registro.descripcion_orden || "",
            precio_unitario: registro.precio_unitario || "",
            incluye_igv: Number(registro.incluye_igv) === 1,
            igv: registro.igv || 0,
            detalles,
        });
        setModalLogistica(true);
    };

    const cerrarLogistica = () => {
        setModalLogistica(false);
        setRegistroSeleccionado(null);
        setFormLogistica(FORM_LOGISTICA_INICIAL);
    };

    const abrirAccion = (tipo, registro) => {
        setAccion(tipo);
        setRegistroSeleccionado(registro);
        setObservacionAccion("");
        setFechaPago(new Date().toISOString().slice(0, 10));
        setArchivoPago(null);
        setModalAccion(true);
    };

    const cerrarAccion = () => {
        setModalAccion(false);
        setRegistroSeleccionado(null);
        setAccion("");
    };

    const handleRequerimientoChange = (e) => {
        const { name, value } = e.target;
        setFormRequerimiento(prev => ({ ...prev, [name]: value }));
    };

    const handleLogisticaChange = (e) => {
        const { name, value, checked, type } = e.target;

        if (name === "servicio_id") {
            const servicio = servicios.find(item => Number(item.servicio_id) === Number(value));
            setFormLogistica(prev => ({
                ...prev,
                servicio_id: value,
                proveedor_id: servicio?.proveedor_id || "",
                descripcion_orden: servicio?.descripcion_servicio || "",
                precio_unitario: servicio?.precio || "",
                incluye_igv: Number(servicio?.incluye_igv) === 1,
            }));
            return;
        }

        setFormLogistica(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    };

    const handleDetalleChange = (index, field, value) => {
        setFormLogistica(prev => ({
            ...prev,
            detalles: prev.detalles.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item),
        }));
    };

    const agregarDetalle = () => {
        setFormLogistica(prev => ({ ...prev, detalles: [...prev.detalles, { ...DETALLE_COMPRA_INICIAL }] }));
    };

    const eliminarDetalle = (index) => {
        setFormLogistica(prev => ({
            ...prev,
            detalles: prev.detalles.length === 1 ? prev.detalles : prev.detalles.filter((_, itemIndex) => itemIndex !== index),
        }));
    };

    const guardarRequerimiento = async () => {
        if (!formRequerimiento.tipo_requerimiento || !formRequerimiento.descripcion_solicitud.trim()) {
            mostrarMensaje("Validación", "Complete los campos obligatorios", "peligro");
            return;
        }

        setLoading(true);
        try {
            await ProcesoRequerimientosService.guardarRequerimiento(formRequerimiento);
            cerrarRequerimiento();
            await cargarRegistros();
        } catch (error) {
            mostrarMensaje("Error", obtenerMensajeError(error, "Error al registrar el requerimiento"), "peligro");
        } finally {
            setLoading(false);
        }
    };

    const atenderLogistica = async () => {
        if (!registroSeleccionado) return;

        const esServicio = registroSeleccionado.tipo_requerimiento === "servicio";

        if (!formLogistica.fecha_orden) {
            mostrarMensaje("Validación", "Ingrese la fecha de orden", "peligro");
            return;
        }

        if (esServicio && (!formLogistica.servicio_id || !formLogistica.descripcion_orden || !formLogistica.precio_unitario)) {
            mostrarMensaje("Validación", "Complete los datos del servicio", "peligro");
            return;
        }

        if (!esServicio && (!formLogistica.proveedor_id || formLogistica.detalles.some(item => !item.descripcion_producto || !item.precio_unitario || !item.cantidad))) {
            mostrarMensaje("Validación", "Complete proveedor y productos", "peligro");
            return;
        }

        const payload = esServicio
            ? {
                fecha_orden: formLogistica.fecha_orden,
                servicio_id: formLogistica.servicio_id,
                cantidad: formLogistica.cantidad,
                descripcion_orden: formLogistica.descripcion_orden,
                precio_unitario: formLogistica.precio_unitario,
                incluye_igv: formLogistica.incluye_igv ? 1 : 0,
            }
            : {
                fecha_orden: formLogistica.fecha_orden,
                proveedor_id: formLogistica.proveedor_id,
                igv: formLogistica.igv || 0,
                detalles: formLogistica.detalles,
            };

        setLoading(true);
        try {
            await ProcesoRequerimientosService.atenderLogistica(registroSeleccionado.requerimiento_id, payload);
            cerrarLogistica();
            await cargarRegistros();
        } catch (error) {
            mostrarMensaje("Error", obtenerMensajeError(error, "Error al atender el requerimiento"), "peligro");
        } finally {
            setLoading(false);
        }
    };

    const ejecutarAccion = async () => {
        if (!registroSeleccionado) return;

        if (["observar_jefe", "observar_subgerencia"].includes(accion) && !observacionAccion.trim()) {
            mostrarMensaje("Validación", "Ingrese una observación", "peligro");
            return;
        }

        if (accion === "pagar" && !archivoPago) {
            mostrarMensaje("Validación", "Adjunte la boleta o factura del pago", "peligro");
            return;
        }

        setLoading(true);
        try {
            if (accion === "aprobar_jefe") {
                await ProcesoRequerimientosService.aprobarJefe(registroSeleccionado.requerimiento_id, observacionAccion);
            }

            if (accion === "observar_jefe") {
                await ProcesoRequerimientosService.observarJefe(registroSeleccionado.requerimiento_id, observacionAccion);
            }

            if (accion === "aprobar_subgerencia") {
                await ProcesoRequerimientosService.aprobarSubgerencia(registroSeleccionado.requerimiento_id, observacionAccion);
            }

            if (accion === "observar_subgerencia") {
                await ProcesoRequerimientosService.observarSubgerencia(registroSeleccionado.requerimiento_id, observacionAccion);
            }

            if (accion === "pagar") {
                await ProcesoRequerimientosService.registrarPago(registroSeleccionado.requerimiento_id, fechaPago, archivoPago);
            }

            cerrarAccion();
            await cargarRegistros();
        } catch (error) {
            mostrarMensaje("Error", obtenerMensajeError(error, "Error al procesar la acción"), "peligro");
        } finally {
            setLoading(false);
        }
    };

    const anularRegistro = (registro) => {
        setConfirmacionModal({
            isOpen: true,
            titulo: "Anular requerimiento",
            mensaje: `¿Está seguro de anular el requerimiento ${registro.numero_requerimiento}?`,
            textConfirmar: "Anular",
            variante: "peligro",
            onConfirmar: async () => {
                setConfirmacionModal(prev => ({ ...prev, isOpen: false, onConfirmar: null }));
                setLoading(true);
                try {
                    await ProcesoRequerimientosService.anularRequerimiento(registro.requerimiento_id);
                    await cargarRegistros();
                } catch (error) {
                    mostrarMensaje("Error", obtenerMensajeError(error, "Error al anular el requerimiento"), "peligro");
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const abrirArchivo = (url) => {
        if (!url) {
            mostrarMensaje("Aviso", "No hay boleta o factura adjunta", "peligro");
            return;
        }
        window.open(url, "_blank");
    };

    const drawSignature = (doc, base64, nombre, cargo, x, y) => {
        if (base64) {
            try {
                const format = base64.includes("image/jpeg") || base64.includes("image/jpg") ? "JPEG" : "PNG";
                doc.addImage(base64, format, x - 48, y - 44, 96, 34);
            } catch (e) {
                console.error("Error al agregar firma:", e);
            }
        }
        doc.line(x - 90, y, x + 90, y);
        doc.setFont("helvetica", "bold");
        doc.text(nombre || "Sin firma", x, y + 16, { align: "center" });
        doc.setFont("helvetica", "normal");
        doc.text(cargo || "Cargo no registrado", x, y + 31, { align: "center" });
    };

    const dibujarEncabezadoPdf = async (doc, titulo, numero) => {
        const logo = await loadImg(`${window.location.origin}/logo_mec.png`);
        if (logo) {
            try {
                doc.addImage(logo, "PNG", 44, 28, 44, 28);
            } catch (e) {
                console.error("Error al agregar logo:", e);
            }
        }
        doc.setFont("helvetica", "bold");
        doc.setFontSize(15);
        doc.text(`${titulo} N°${numero}`, 300, 48, { align: "center" });
    };

    const numeroOrdenPdf = (registro) => String(registro.numero_orden || "").replace(/^[A-Z]+-/, "") || "-";

    const generarPdfServicio = async (registro, imprimir = false) => {
        const doc = new jsPDF("p", "pt", "a4");
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 48;
        const right = pageWidth - margin;
        const detalle = registro.detalles?.[0];

        await dibujarEncabezadoPdf(doc, "ORDEN DE SERVICIO", numeroOrdenPdf(registro));

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(`Fecha: ${formatoFecha(registro.fecha_orden)}`, right, 76, { align: "right" });

        doc.setFont("helvetica", "bold");
        doc.text("Cliente", margin, 116);
        doc.setFont("helvetica", "normal");
        doc.text(`Razón social: ${CLIENTE.razonSocial}`, margin, 134);
        doc.text(`RUC: ${CLIENTE.ruc}`, margin, 152);
        doc.text(`Dirección: ${CLIENTE.direccion}`, margin, 170);

        doc.setFont("helvetica", "bold");
        doc.text("Proveedor", margin, 206);
        doc.setFont("helvetica", "normal");
        doc.text(`Razón social: ${registro.proveedor_razon_social || "-"}`, margin, 224);
        doc.text(`${registro.proveedor_tipo_documento || "RUC"}: ${registro.proveedor_documento || "-"}`, margin, 242);

        const tableTop = 278;
        doc.setFont("helvetica", "bold");
        doc.rect(margin, tableTop, pageWidth - margin * 2, 28);
        doc.text("Cantidad", margin + 14, tableTop + 18);
        doc.text("Descripción del Servicio", margin + 100, tableTop + 18);
        doc.text("Precio Unitario", right - 92, tableTop + 18);

        doc.setFont("helvetica", "normal");
        const descripcion = doc.splitTextToSize(detalle?.descripcion_producto || registro.descripcion_orden || "-", 292);
        const rowHeight = Math.max(56, descripcion.length * 14 + 22);
        doc.rect(margin, tableTop + 28, pageWidth - margin * 2, rowHeight);
        doc.text(String(detalle?.cantidad || registro.cantidad || 1), margin + 32, tableTop + 55);
        doc.text(descripcion, margin + 100, tableTop + 55);
        doc.text(formatoSoles(detalle?.precio_unitario || registro.precio_unitario), right - 18, tableTop + 55, { align: "right" });

        const totalsTop = tableTop + 28 + rowHeight;
        doc.rect(right - 190, totalsTop, 190, 78);
        doc.text("Subtotal", right - 178, totalsTop + 20);
        doc.text(formatoSoles(registro.neto), right - 18, totalsTop + 20, { align: "right" });
        doc.text(Number(registro.igv || 0) > 0 ? "I.G.V. (18%)" : "I.G.V.", right - 178, totalsTop + 44);
        doc.text(formatoSoles(registro.igv), right - 18, totalsTop + 44, { align: "right" });
        doc.setFont("helvetica", "bold");
        doc.text("Total", right - 178, totalsTop + 68);
        doc.text(formatoSoles(registro.total), right - 18, totalsTop + 68, { align: "right" });

        const obsTop = totalsTop + 122;
        doc.setFont("helvetica", "bold");
        doc.text("Observaciones", margin, obsTop);
        doc.setFont("helvetica", "normal");
        doc.text("Realizar el pago en:", margin, obsTop + 22);
        doc.text(`Cuenta ${registro.tipo_banco || "-"}: ${registro.numero_cuenta || "-"}`, margin, obsTop + 44);
        doc.text(`Cuenta interbancaria: ${registro.cuenta_interbancaria || "-"}`, margin, obsTop + 66);

        const firmaTop = 700;
        // Firma de Logística comentada temporalmente para evitar repetición con Subgerencia
        /* drawSignature(
            doc,
            registro.firma_logistica_base64,
            registro.atendido_por_nombre || "Logística",
            registro.atendido_por_cargo || "Logística",
            margin + 102,
            firmaTop
        ); */
        drawSignature(
            doc,
            registro.firma_subgerencia_base64,
            registro.subgerencia_aprobador_nombre || "Subgerencia de Administración",
            registro.subgerencia_aprobador_cargo || "Subgerencia de Administración",
            pageWidth / 2,
            firmaTop
        );

        if (imprimir) doc.autoPrint();
        window.open(doc.output("bloburl"), "_blank");
    };

    const generarPdfCompra = async (registro, imprimir = false) => {
        const doc = new jsPDF("p", "pt", "a4");
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 42;
        const right = pageWidth - margin;
        let y = 92;

        await dibujarEncabezadoPdf(doc, "ORDEN DE COMPRA", numeroOrdenPdf(registro));

        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.text("DATOS DEL CLIENTE Y DE LA FACTURA", margin, y);
        y += 12;
        doc.setFont("helvetica", "normal");
        doc.text(`Razón Social: ${CLIENTE.razonSocial}`, margin, y);
        doc.text(`Ciudad: ${CLIENTE.ciudad}`, 370, y);
        y += 16;
        doc.text(`Dirección: ${CLIENTE.direccion}`, margin, y);
        doc.text(`RUC: ${CLIENTE.ruc}`, 370, y);
        y += 28;

        doc.setFont("helvetica", "bold");
        doc.text("DATOS DEL PROVEEDOR", margin, y);
        y += 12;
        doc.setFont("helvetica", "normal");
        doc.text(`Razón social: ${registro.proveedor_razon_social || "-"}`, margin, y);
        doc.text("Ciudad: -", 370, y);
        y += 16;
        doc.text("Dirección: -", margin, y);
        doc.text(`${registro.proveedor_tipo_documento || "RUC"}: ${registro.proveedor_documento || "-"}`, 370, y);
        y += 30;

        doc.setFont("helvetica", "bold");
        doc.text("DATOS DEL PRODUCTO A ADQUIRIR", margin, y);
        y += 10;

        const col = {
            parte: margin,
            desc: margin + 95,
            precio: right - 170,
            cant: right - 85,
            total: right - 10,
        };

        doc.rect(margin, y, right - margin, 24);
        doc.text("No. Parte / Tipo Modelo", col.parte + 4, y + 15);
        doc.text("Descripción del Producto", col.desc + 4, y + 15);
        doc.text("Precio Unitario", col.precio, y + 15, { align: "right" });
        doc.text("Cant", col.cant, y + 15, { align: "right" });
        doc.text("Precio Total", col.total, y + 15, { align: "right" });
        y += 24;

        doc.setFont("helvetica", "normal");
        (registro.detalles || []).forEach((detalle) => {
            const descripcion = doc.splitTextToSize(detalle.descripcion_producto || "-", 210);
            const rowHeight = Math.max(24, descripcion.length * 12 + 10);
            doc.rect(margin, y, right - margin, rowHeight);
            doc.text(String(detalle.no_parte_tipo_modelo || "-"), col.parte + 4, y + 15);
            doc.text(descripcion, col.desc + 4, y + 15);
            doc.text(formatoSoles(detalle.precio_unitario), col.precio, y + 15, { align: "right" });
            doc.text(String(detalle.cantidad || 0), col.cant, y + 15, { align: "right" });
            doc.text(formatoSoles(detalle.precio_total), col.total, y + 15, { align: "right" });
            y += rowHeight;
        });

        y += 6;
        doc.setFont("helvetica", "bold");
        doc.text("NETO", col.precio, y + 14, { align: "right" });
        doc.text(formatoSoles(registro.neto), col.total, y + 14, { align: "right" });
        y += 18;
        doc.text("IGV", col.precio, y + 14, { align: "right" });
        doc.text(formatoSoles(registro.igv), col.total, y + 14, { align: "right" });
        y += 18;
        doc.text("TOTAL", col.precio, y + 14, { align: "right" });
        doc.text(formatoSoles(registro.total), col.total, y + 14, { align: "right" });

        const firmaTop = 705;
        // Firma de Logística comentada temporalmente para evitar repetición con Subgerencia
        /* drawSignature(
            doc,
            registro.firma_logistica_base64,
            registro.atendido_por_nombre || "Logística",
            registro.atendido_por_cargo || "Logística",
            margin + 105,
            firmaTop
        ); */
        drawSignature(
            doc,
            registro.firma_subgerencia_base64,
            registro.subgerencia_aprobador_nombre || "Subgerencia de Administración",
            registro.subgerencia_aprobador_cargo || "Subgerencia de Administración",
            pageWidth / 2,
            firmaTop
        );

        if (imprimir) doc.autoPrint();
        window.open(doc.output("bloburl"), "_blank");
    };

    const generarPdfOrden = async (registro, imprimir = false) => {
        if (!registro.numero_orden) {
            mostrarMensaje("Aviso", "El requerimiento aún no tiene orden generada", "peligro");
            return;
        }

        if (registro.tipo_requerimiento === "compra") {
            await generarPdfCompra(registro, imprimir);
            return;
        }

        await generarPdfServicio(registro, imprimir);
    };

    const puedeValidarJefe = (registro) => Number(registro.estado_proceso) === 1 && Number(registro.jefe_perfil_id) === perfilId;
    const puedeAtenderLogistica = (registro) => puedeLogistica && [3, 7].includes(Number(registro.estado_proceso));
    const puedeValidarSubgerencia = (registro) => puedeSubgerencia && Number(registro.estado_proceso) === 4;
    const puedePagar = (registro) => puedeTesoreria && Number(registro.estado_proceso) === 5;
    const puedeAnular = (registro) => Number(registro.estado_proceso) !== 6 && (puedeLogistica || puedeSubgerencia || Number(registro.solicitante_id) === usuarioId);
    const puedeVerOrden = (registro) => Boolean(registro.numero_orden);

    const claseFilaEstado = (estadoProceso) => {
        const actual = Number(estadoProceso);
        if ([2, 7].includes(actual)) return "fila-observada";
        if (actual === 6) return "fila-finalizada";
        return "";
    };

    const tituloAccion = () => {
        if (accion === "aprobar_jefe") return "APROBAR REQUERIMIENTO";
        if (accion === "observar_jefe") return "OBSERVAR REQUERIMIENTO";
        if (accion === "aprobar_subgerencia") return "APROBAR SUBGERENCIA";
        if (accion === "observar_subgerencia") return "OBSERVAR SUBGERENCIA";
        if (accion === "pagar") return "REGISTRAR PAGO";
        return "ACCIÓN";
    };

    return (
        <div className="caja-flujo-container proceso-requerimientos-scope">
            {loading && <Loader />}

            <div className="flex items-center justify-between mb-4">
                <h1>PROCESO DE REQUERIMIENTOS</h1>
            </div>

            <div className="filtros">
                <div className="campo-fecha">
                    <Label>Periodo:</Label>
                    <Input type="month" value={periodo} onChange={(e) => setPeriodo(e.target.value)} />
                </div>
                <div className="campo-fecha">
                    <Label>Estado:</Label>
                    <Input type="select" value={estado} onChange={(e) => setEstado(e.target.value)}>
                        <option value="">Todos los estados</option>
                        {ESTADOS.map(item => <option key={item.id} value={item.id}>{item.nombre}</option>)}
                    </Input>
                </div>
            </div>

            <ButtonSearch onClick={cargarRegistros} isLoading={loading} />

            <div className="modulo-toolbar">
                <ButtonInsert onClick={abrirNuevo} />
            </div>

            <div className="numero-propuestas">
                <p>N° requerimientos: {registros.length}</p>
                <p>Total atendido: <span>{formatoSoles(totalRequerimientos)}</span></p>
            </div>

            <div className="table-container">
                <Table>
                    <thead>
                        <tr>
                            <th>N° REQ.</th>
                            <th>FECHA</th>
                            <th>SOLICITANTE</th>
                            <th>TIPO</th>
                            <th>REQUERIMIENTO</th>
                            <th>N° ORDEN</th>
                            <th>PROVEEDOR</th>
                            <th>TOTAL</th>
                            <th>ESTADO</th>
                            <th>OBSERVACIÓN</th>
                            <th>BOLETA / FACTURA</th>
                            <th>ACCIONES</th>
                        </tr>
                    </thead>
                    <tbody>
                        {registros.length === 0 ? (
                            <tr><td colSpan="12" className="sin-data">No hay requerimientos para el periodo seleccionado</td></tr>
                        ) : registros.map(registro => (
                            <tr key={registro.requerimiento_id} className={claseFilaEstado(registro.estado_proceso)}>
                                <td>{registro.numero_requerimiento}</td>
                                <td>{formatoFecha(registro.fecha_solicitud)}</td>
                                <td>{registro.solicitante_nombre}</td>
                                <td>{registro.tipo_requerimiento === "compra" ? "Compra" : "Servicio"}</td>
                                <td className="descripcion-col">{registro.descripcion_solicitud}</td>
                                <td>{registro.numero_orden || "-"}</td>
                                <td>{registro.proveedor_razon_social || "-"}</td>
                                <td>{registro.total ? formatoSoles(registro.total) : "-"}</td>
                                <td className="estado-proceso">{registro.estado_nombre}</td>
                                <td className="observacion-col">{registro.observacion_jefe || registro.observacion_subgerencia || "-"}</td>
                                <td className="documento-cell">
                                    <button type="button" className={`documento-icono ${!registro.archivo_pago_url ? "sin-archivo" : ""}`} onClick={() => abrirArchivo(registro.archivo_pago_url)} title="Ver boleta o factura">
                                        <FileMinus size={20} style={{ color: registro.archivo_pago_url ? "#3cb167" : "#f87171" }} />
                                    </button>
                                </td>
                                <td>
                                    <div className="acciones-container">
                                        {puedeVerOrden(registro) && (
                                            <>
                                                <button type="button" className="boton-pdf" onClick={() => generarPdfOrden(registro)} title="Ver orden PDF">
                                                    <FileText size={16} />
                                                </button>
                                                <button type="button" className="boton-imprimir" onClick={() => generarPdfOrden(registro, true)} title="Imprimir orden">
                                                    <Printer size={16} />
                                                </button>
                                            </>
                                        )}
                                        {puedeValidarJefe(registro) && (
                                            <>
                                                <button type="button" className="boton-aprobar" onClick={() => abrirAccion("aprobar_jefe", registro)}>Aprobar</button>
                                                <button type="button" className="boton-observar" onClick={() => abrirAccion("observar_jefe", registro)}>Observar</button>
                                            </>
                                        )}
                                        {puedeAtenderLogistica(registro) && (
                                            <button type="button" className="boton-logistica" onClick={() => abrirLogistica(registro)}>Atender</button>
                                        )}
                                        {puedeValidarSubgerencia(registro) && (
                                            <>
                                                <button type="button" className="boton-aprobar" onClick={() => abrirAccion("aprobar_subgerencia", registro)}>Aprobar</button>
                                                <button type="button" className="boton-observar" onClick={() => abrirAccion("observar_subgerencia", registro)}>Observar</button>
                                            </>
                                        )}
                                        {puedePagar(registro) && (
                                            <button type="button" className="boton-pagar" onClick={() => abrirAccion("pagar", registro)}>Pagar</button>
                                        )}
                                        {puedeAnular(registro) && <ButtonDelete onClick={() => anularRegistro(registro)} />}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            <Modal isOpen={modalRequerimiento} className="custom-modal" backdrop="static" size="lg" fade={false}>
                <ModalHeader className="modal-header">
                    <div className="titulo">
                        <h1>REGISTRAR REQUERIMIENTO</h1>
                        <Button className="close-btn" onClick={cerrarRequerimiento}>X</Button>
                    </div>
                </ModalHeader>
                <div className="modal-body-footer">
                    <ModalBody className="modal-body">
                        <Form className="fila" onSubmit={(e) => e.preventDefault()}>
                            <FormGroup>
                                <Label>Tipo:</Label>
                                <Input className="form-control" type="select" name="tipo_requerimiento" value={formRequerimiento.tipo_requerimiento} onChange={handleRequerimientoChange}>
                                    <option value="servicio">Servicio</option>
                                    <option value="compra">Compra de bien</option>
                                </Input>
                            </FormGroup>
                            <FormGroup className="full-width">
                                <Label>Requerimiento:</Label>
                                <Input className="form-control" type="textarea" name="descripcion_solicitud" value={formRequerimiento.descripcion_solicitud} onChange={handleRequerimientoChange} />
                            </FormGroup>
                            <FormGroup>
                                <div className="button-container">
                                    <Button className="button-custom" onClick={() => setConfirmacionModal({
                                        isOpen: true,
                                        titulo: "Confirmar registro",
                                        mensaje: "¿Está seguro de guardar este requerimiento?",
                                        textConfirmar: "Guardar",
                                        variante: "exito",
                                        onConfirmar: async () => {
                                            setConfirmacionModal(prev => ({ ...prev, isOpen: false, onConfirmar: null }));
                                            await guardarRequerimiento();
                                        },
                                    })}>
                                        GUARDAR
                                    </Button>
                                </div>
                            </FormGroup>
                        </Form>
                    </ModalBody>
                </div>
            </Modal>

            <Modal isOpen={modalLogistica} className="custom-modal" backdrop="static" size="lg" fade={false}>
                <ModalHeader className="modal-header">
                    <div className="titulo">
                        <h1>ATENDER REQUERIMIENTO</h1>
                        <Button className="close-btn" onClick={cerrarLogistica}>X</Button>
                    </div>
                </ModalHeader>
                <div className="modal-body-footer">
                    <ModalBody className="modal-body">
                        <Form className="fila" onSubmit={(e) => e.preventDefault()}>
                            <FormGroup>
                                <Label>Fecha orden:</Label>
                                <Input className="form-control" type="date" name="fecha_orden" value={formLogistica.fecha_orden} onChange={handleLogisticaChange} />
                            </FormGroup>

                            {registroSeleccionado?.tipo_requerimiento === "servicio" ? (
                                <>
                                    <FormGroup className="full-width">
                                        <Label>Servicio registrado:</Label>
                                        <Input className="form-control" type="select" name="servicio_id" value={formLogistica.servicio_id} onChange={handleLogisticaChange}>
                                            <option value="">Seleccione</option>
                                            {servicios.map(servicio => (
                                                <option key={servicio.servicio_id} value={servicio.servicio_id}>{servicio.nombre_razon_social} - {servicio.descripcion_servicio}</option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                    <FormGroup>
                                        <Label>Cantidad:</Label>
                                        <Input className="form-control" type="number" min="1" name="cantidad" value={formLogistica.cantidad} onChange={handleLogisticaChange} />
                                    </FormGroup>
                                    <FormGroup>
                                        <Label>Precio unitario:</Label>
                                        <div className="input-group custom-input-group">
                                            <span className="input-group-text custom-symbol">S/.</span>
                                            <Input className="form-control" type="number" step="0.01" name="precio_unitario" value={formLogistica.precio_unitario} onChange={handleLogisticaChange} />
                                        </div>
                                    </FormGroup>
                                    <FormGroup>
                                        <Label>Incluye IGV:</Label>
                                        <div>
                                            <Input type="checkbox" name="incluye_igv" checked={formLogistica.incluye_igv} onChange={handleLogisticaChange} />
                                        </div>
                                    </FormGroup>
                                    <FormGroup>
                                        <Label>Total:</Label>
                                        <Input className="form-control" value={formatoSoles(totalServicio)} disabled />
                                    </FormGroup>
                                    <FormGroup className="full-width">
                                        <Label>Descripción de orden:</Label>
                                        <Input className="form-control" type="textarea" name="descripcion_orden" value={formLogistica.descripcion_orden} onChange={handleLogisticaChange} />
                                    </FormGroup>
                                </>
                            ) : (
                                <>
                                    <FormGroup className="full-width">
                                        <Label>Proveedor:</Label>
                                        <Input className="form-control" type="select" name="proveedor_id" value={formLogistica.proveedor_id} onChange={handleLogisticaChange}>
                                            <option value="">Seleccione</option>
                                            {proveedores.map(proveedor => (
                                                <option key={proveedor.proveedor_id} value={proveedor.proveedor_id}>{proveedor.nombre_razon_social} - {proveedor.tipo_documento}: {proveedor.documento}</option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                    <div className="detalles-compra full-width">
                                        <div className="detalles-header">
                                            <Label>Productos:</Label>
                                            <button type="button" className="boton-icono-agregar" onClick={agregarDetalle} title="Agregar producto">
                                                <Plus size={16} />
                                            </button>
                                        </div>
                                        {formLogistica.detalles.map((detalle, index) => (
                                            <div className="detalle-row" key={`detalle-${index}`}>
                                                <Input className="form-control" placeholder="No. Parte / tipo modelo" value={detalle.no_parte_tipo_modelo} onChange={(e) => handleDetalleChange(index, "no_parte_tipo_modelo", e.target.value)} />
                                                <Input className="form-control detalle-descripcion" placeholder="Descripción del producto" value={detalle.descripcion_producto} onChange={(e) => handleDetalleChange(index, "descripcion_producto", e.target.value)} />
                                                <Input className="form-control" type="number" step="0.01" placeholder="Precio" value={detalle.precio_unitario} onChange={(e) => handleDetalleChange(index, "precio_unitario", e.target.value)} />
                                                <Input className="form-control" type="number" min="1" placeholder="Cant." value={detalle.cantidad} onChange={(e) => handleDetalleChange(index, "cantidad", e.target.value)} />
                                                <Input className="form-control" value={formatoSoles(Number(detalle.precio_unitario || 0) * Number(detalle.cantidad || 0))} disabled />
                                                <button type="button" className="boton-icono-eliminar" onClick={() => eliminarDetalle(index)} title="Quitar producto">
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    <FormGroup>
                                        <Label>Neto:</Label>
                                        <Input className="form-control" value={formatoSoles(netoCompra)} disabled />
                                    </FormGroup>
                                    <FormGroup>
                                        <Label>IGV:</Label>
                                        <div className="input-group custom-input-group">
                                            <span className="input-group-text custom-symbol">S/.</span>
                                            <Input className="form-control" type="number" step="0.01" name="igv" value={formLogistica.igv} onChange={handleLogisticaChange} />
                                        </div>
                                    </FormGroup>
                                    <FormGroup>
                                        <Label>Total:</Label>
                                        <Input className="form-control" value={formatoSoles(totalCompra)} disabled />
                                    </FormGroup>
                                </>
                            )}

                            <FormGroup>
                                <div className="button-container">
                                    <Button className="button-custom" onClick={() => setConfirmacionModal({
                                        isOpen: true,
                                        titulo: "Confirmar atención",
                                        mensaje: "¿Está seguro de generar la orden para este requerimiento?",
                                        textConfirmar: "Generar",
                                        variante: "exito",
                                        onConfirmar: async () => {
                                            setConfirmacionModal(prev => ({ ...prev, isOpen: false, onConfirmar: null }));
                                            await atenderLogistica();
                                        },
                                    })}>
                                        GENERAR ORDEN
                                    </Button>
                                </div>
                            </FormGroup>
                        </Form>
                    </ModalBody>
                </div>
            </Modal>

            <Modal isOpen={modalAccion} className="custom-modal" backdrop="static" fade={false}>
                <ModalHeader className="modal-header">
                    <div className="titulo">
                        <h1>{tituloAccion()}</h1>
                        <Button className="close-btn" onClick={cerrarAccion}>X</Button>
                    </div>
                </ModalHeader>
                <div className="modal-body-footer">
                    <ModalBody className="modal-body">
                        <Form className="fila" onSubmit={(e) => e.preventDefault()}>
                            {accion === "pagar" && (
                                <>
                                    <FormGroup>
                                        <Label>Fecha de pago:</Label>
                                        <Input className="form-control" type="date" value={fechaPago} onChange={(e) => setFechaPago(e.target.value)} />
                                    </FormGroup>
                                    <FormGroup>
                                        <Label>Boleta o factura:</Label>
                                        <Input className="form-control" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setArchivoPago(e.target.files[0])} />
                                    </FormGroup>
                                </>
                            )}
                            {accion !== "pagar" && (
                                <FormGroup className="full-width">
                                    <Label>Observación:</Label>
                                    <Input className="form-control" type="textarea" value={observacionAccion} onChange={(e) => setObservacionAccion(e.target.value)} />
                                </FormGroup>
                            )}
                            <FormGroup>
                                <div className="button-container">
                                    <Button className="button-custom" onClick={() => setConfirmacionModal({
                                        isOpen: true,
                                        titulo: "Confirmar acción",
                                        mensaje: "¿Está seguro de realizar esta acción?",
                                        textConfirmar: "Confirmar",
                                        variante: "exito",
                                        onConfirmar: async () => {
                                            setConfirmacionModal(prev => ({ ...prev, isOpen: false, onConfirmar: null }));
                                            await ejecutarAccion();
                                        },
                                    })}>
                                        CONFIRMAR
                                    </Button>
                                </div>
                            </FormGroup>
                        </Form>
                    </ModalBody>
                </div>
            </Modal>

            <ModalConfirmar
                isOpen={mensajeModal.isOpen}
                titulo={mensajeModal.titulo}
                mensaje={mensajeModal.mensaje}
                textConfirmar="Aceptar"
                textCancelar="Cerrar"
                variante={mensajeModal.variante}
                onConfirmar={() => setMensajeModal(prev => ({ ...prev, isOpen: false }))}
                onCancelar={() => setMensajeModal(prev => ({ ...prev, isOpen: false }))}
            />
            <ModalConfirmar
                isOpen={confirmacionModal.isOpen}
                titulo={confirmacionModal.titulo}
                mensaje={confirmacionModal.mensaje}
                textConfirmar={confirmacionModal.textConfirmar}
                textCancelar="Cancelar"
                variante={confirmacionModal.variante}
                onConfirmar={() => confirmacionModal.onConfirmar && confirmacionModal.onConfirmar()}
                onCancelar={() => setConfirmacionModal(prev => ({ ...prev, isOpen: false, onConfirmar: null }))}
            />
        </div>
    );
}

export default ProcesoRequerimientos;
